/**
 * Changer le modèle d'une journée déjà ouverte.
 *
 * `planning_override` ne joue qu'à la **création** de la journée (§4.5) : une
 * fois la journée née, surcharger le planning n'a plus aucun effet sur elle. Il
 * fallait donc un geste explicite pour « finalement, aujourd'hui c'est jour
 * off », y compris — et surtout — quand un modèle est déjà appliqué.
 *
 * ⚠️ C'est un **nouveau snapshot**, jamais un abonnement (specs §7.4) : la
 * journée recopie les objectifs du modèle à cet instant, puis reprend sa vie
 * indépendante. Modifier le modèle ensuite ne la touchera pas davantage
 * qu'avant.
 *
 * ⚠️ **Aucune entrée n'est touchée.** Les repas déjà présents gardent leurs
 * saisies ; seuls les repas **vides** absents du nouveau modèle sont retirés,
 * pour que changer de modèle change réellement la journée. Un repas qui porte
 * une entrée reste, même s'il n'appartient pas au nouveau modèle — perdre une
 * saisie pour une raison de mise en forme serait inacceptable (§4.1).
 */

import { useQuery } from '@tanstack/react-query';

import { fetchTemplates } from '../../api/templates';
import { Badge } from '../../components/nourri';
import { Hint, Sheet } from '../../components/ui/primitives';

export function TemplateSheet({
  open,
  currentTemplateId,
  onClose,
  onPick,
}: {
  open: boolean;
  currentTemplateId: number | null;
  onClose: () => void;
  onPick: (templateId: number) => void;
}) {
  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates, enabled: open });
  const list = templates.data ?? [];

  return (
    <Sheet open={open} title="Modèle de la journée" onClose={onClose}>
      {list.length === 0 ? (
        <Hint>
          Aucun modèle pour l’instant. En créer un dans l’onglet Bibliothèque : il portera les
          objectifs P/G/L/kcal et la liste des repas.
        </Hint>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {list.map((template) => {
              const current = template.id === currentTemplateId;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => {
                    onPick(template.id);
                    onClose();
                  }}
                  className="n-pick"
                  aria-pressed={current}
                >
                  <span className="n-pick__main">
                    <span className="n-pick__name">{template.name}</span>
                    <span className="n-pick__meta tnum">
                      {template.meals.length} repas ·{' '}
                      {Math.round(template.targets.kcal).toLocaleString('fr-FR')} kcal
                    </span>
                  </span>
                  {/*
                    ⚠️ Le **mot** dit l'état, la teinte ne fait que le doubler :
                    un modèle appliqué se reconnaît sans distinguer les couleurs.
                  */}
                  {current && <Badge tone="brand">appliqué</Badge>}
                </button>
              );
            })}
          </div>
          <Hint>
            Les objectifs de la journée sont recopiés depuis le modèle. Les repas déjà remplis sont
            conservés avec leurs entrées ; les repas vides absents du nouveau modèle sont retirés.
          </Hint>
        </>
      )}
    </Sheet>
  );
}
